/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Render the per-student record inside a class.
 * @state-owner dataset store
 * @allowed-imports React; dataset and workspace stores; shared components, selectors and hooks
 * @forbidden direct API client; student-only surfaces
 * @public-api workspace main column
 * @side-effects navigation to reports and live intervention
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useEffect } from 'react';
import { useActiveContextId } from '../../../app/providers/ActiveRoleProvider';
import { BackButton, WrapPage } from '../../../shared/components/PageShell';
import { hasHttpStatus } from '../../../shared/lib/api-error';
import { RUN_MODE_LABEL, RUN_STATUS_LABEL } from '../../../shared/lib/demo-data';
import {
  classRuns,
  findSeat,
  runName,
  seatPresentation,
  seatStatusLabel,
} from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { useClassroomStudentSummary, useClassStudentsFiltered } from '../api/classrooms.queries';
import { useClassroomUiStore } from '../store/classroom-ui-store';

export function studentSummarySignalLabel(signal: string): string {
  switch (signal) {
    case 'submission_not_released':
      return '尚未發布結果';
    case 'partial_released_result':
      return '已發布結果未滿分';
    case 'correction_submission':
      return '修正提交';
    default:
      return signal;
  }
}

export function selectStableStudent<T extends { userId: string; name: string }>(
  members: readonly T[] | undefined,
  selectedUserId: string | null,
  displayName: string,
): T | undefined {
  if (selectedUserId) return members?.find((member) => member.userId === selectedUserId);
  const matches = members?.filter((member) => member.name === displayName) ?? [];
  return matches.length === 1 ? matches[0] : undefined;
}

export function StudentPage({ classIndex, student }: { classIndex: number; student: string }) {
  const t = useTranslator();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);
  const seats = useDatasetStore((state) => state.seats);
  const closeItem = useWorkspaceUiStore((state) => state.closeItem);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const openItem = useWorkspaceUiStore((state) => state.openItem);
  const activeContextId = useActiveContextId();
  const selectedStudentUserId = useClassroomUiStore((state) => state.selectedStudentUserId);
  const setStudentSelection = useClassroomUiStore((state) => state.setStudentSelection);
  const focusedStudentRowId = useClassroomUiStore((state) => state.focusedStudentRowId);
  const classroom = classes[classIndex];
  const members = useClassStudentsFiltered(activeContextId, classroom);
  const selectedMember = selectStableStudent(members.data, selectedStudentUserId, student);
  const summary = useClassroomStudentSummary(
    activeContextId,
    classroom.id,
    selectedMember?.userId ?? null,
  );
  useEffect(() => {
    if (
      summary.isError &&
      (hasHttpStatus(summary.error, 403) || hasHttpStatus(summary.error, 404))
    ) {
      closeItem();
      setStudentSelection(null, null);
    }
  }, [closeItem, setStudentSelection, summary.error, summary.isError]);

  const rows = classRuns(runs, classIndex);
  const seat = findSeat(seats[classIndex] ?? [], student);
  const live = rows.some((run) => run.status === 'live');
  const others = classes
    .map((_, index) => index)
    .filter((index) => index !== classIndex && classes[index].students.includes(student));

  const openIntervention = () => {
    const run = rows.find((item) => item.status === 'live');
    if (!run) return;
    navigate('live', run.id, { open: { k: 'iv', n: student } });
  };

  const openRun = (runId: string) => {
    const run = runs.find((item) => item.id === runId);
    if (!run) return;
    if (run.status === 'live') {
      navigate('live', runId);
      return;
    }
    openItem({ k: run.status === 'open' ? 'prog' : 'rev', id: runId });
  };

  const summaryState = summary.isLoading ? (
    <p role="status">載入學生摘要中…</p>
  ) : summary.isError ? (
    <p role="alert">
      {hasHttpStatus(summary.error, 403)
        ? '此班級無法查看。'
        : hasHttpStatus(summary.error, 404)
          ? '找不到這位班級學生。'
          : hasHttpStatus(summary.error, 422)
            ? '摘要期間無效。'
            : '摘要暫時無法使用，請稍後再試。'}
    </p>
  ) : summary.data?.records.length ? (
    <ul aria-label="學生提交來源">
      {summary.data.records.map((record) => (
        <li key={record.submission_id}>
          <strong>{record.kind === 'correction' ? '修正提交' : '提交'}</strong> · {record.source} ·{' '}
          {new Date(record.submitted_at).toLocaleString('zh-TW')} ·{' '}
          {record.released_result ? (
            <span>
              {record.released_result.total_score} / {record.released_result.max_score} 分 ·{' '}
              {record.released_result.overall_feedback ?? '沒有回饋'} · 來源：
              {record.released_result.release_source} · 可用於{' '}
              {new Date(record.released_result.available_at).toLocaleString('zh-TW')} · 發布於{' '}
              {new Date(record.released_result.released_at).toLocaleString('zh-TW')}
            </span>
          ) : (
            '尚無已發布結果'
          )}{' '}
          · {record.attention_signals.map(studentSummarySignalLabel).join('、') || '已發布結果'}
        </li>
      ))}
    </ul>
  ) : (
    <p>這段期間沒有提交紀錄。</p>
  );

  return (
    <WrapPage maxWidth={900}>
      <div className="hd">
        <BackButton
          onSelect={() => {
            setStudentSelection(null, focusedStudentRowId);
            closeItem();
          }}
          title={`${t('返回')} ${classes[classIndex].name}`}
        />
        <div>
          <h1>{student}</h1>
          <p className="sub">
            {`${live && seat ? seatStatusLabel(seat, t) : t('目前沒有進行中的活動')}${
              others.length
                ? ` · ${t('也在')} ${others.map((index) => classes[index].name).join('、')}`
                : ''
            }`}
          </p>
        </div>
        <div className="sp" />
        <button className="bt" type="button" onClick={() => navigate('rep', student)}>
          {t('檢視學習報告')}
        </button>
        {live && seat && seatPresentation(seat.status).needsTeacher ? (
          <button className="bt pri" type="button" onClick={openIntervention}>
            {t('開啟接手包')}
          </button>
        ) : null}
      </div>
      <section className="panel flush">
        <header>
          <h2>{t('在這個班的紀錄')}</h2>
          <span className="n">{`${rows.length} 份`}</span>
        </header>
        <div>
          {rows.map((run) => (
            <button className="it" key={run.id} type="button" onClick={() => openRun(run.id)}>
              <div className="av">{t('題')}</div>
              <div className="t">
                <b>{runName(activities, run)}</b>
                <span>
                  {`${t(run.dateLabel)} · ${t(RUN_MODE_LABEL[run.mode])} · ${t(RUN_STATUS_LABEL[run.status])}`}
                </span>
              </div>
              <em>{run.status === 'live' ? (seat ? seatStatusLabel(seat, t) : '') : '88 分'}</em>
            </button>
          ))}
        </div>
      </section>
      <section className="panel flush" aria-label="學生摘要">
        <header>
          <h2>來源摘要</h2>
          <span className="n">近 30 天</span>
        </header>
        <div data-summary-state="success">{summaryState}</div>
      </section>
    </WrapPage>
  );
}
